/** The kit's commands: undo, view switches, and the cave buttons TEST SAVE SHARE NEW LIBRARY BACK. */

import { decodeCave, encodeCave } from '../../caves/codec';
import type { CaveDef } from '../../caves/types';
import { docFromDef, newDoc } from '../model/doc';
import { zoomTo } from '../model/view';
import type { Command, KitApi } from './api';
import { issueLine } from './issues';
import { INK, VIEW } from './layout';
import { confirmModal } from './modals/confirm';
import { helpModal } from './modals/help';
import { libraryModal } from './modals/library';
import { shareModal } from './modals/share';

/** Ask before `then` throws away unsaved changes. */
function unlessUnsaved(k: KitApi, title: string, what: string, then: () => void): void {
  if (!k.st.dirty) return then();
  k.openModal(confirmModal(k, {
    title,
    lines: ['THE CAVE ON THE BENCH HAS UNSAVED CHANGES.', what],
    yes: 'GO ON', no: 'CANCEL', danger: true, onYes: then,
  }));
}

function test(k: KitApi): void {
  const info = k.st.info;
  const first = info.issues.find((i) => i.severity === 'error');
  if (first) {
    k.sound('error');
    return k.st.say(`FIX THE ERRORS FIRST: ${issueLine(first)}`, INK.error);
  }
  k.painter.cancel();
  k.st.persist();
  k.sound('select');
  k.host.push({ to: 'play', request: { mode: 'practice', def: info.def, level: 1, back: 'pop' } });
}

function save(k: KitApi): void {
  const lib = k.host.services.library;
  const { def, errors } = k.st.info;
  const write = (): void => {
    const key = lib.save(def, Date.now(), k.st.libraryKey);
    if (!lib.get(key)) {
      k.sound('error');
      return k.st.say('COULD NOT SAVE: THE BROWSER STORAGE IS FULL OR SWITCHED OFF', INK.error);
    }
    k.st.markSaved(key);
    k.sound('select');
    const note = errors > 0 ? 'FIX ITS ERRORS BEFORE IT CAN BE PLAYED' : 'TO YOUR LIBRARY';
    k.st.say(`SAVED ${def.name} ${note}`, INK.ok);
  };
  const caves = lib.list();
  const updating = caves.some((c) => c.key === k.st.libraryKey);
  const oldest = caves[caves.length - 1];
  if (!updating && caves.length >= 60 && oldest) {
    return k.openModal(confirmModal(k, {
      title: 'THE LIBRARY IS FULL',
      lines: ['YOUR LIBRARY HOLDS 60 CAVES.', `SAVING THIS ONE DROPS THE OLDEST: ${oldest.def.name}.`],
      yes: 'SAVE', no: 'CANCEL', danger: true, onYes: write,
    }));
  }
  write();
}

/** A cave from a pasted code or link: onto the bench, asking first if that loses changes. */
export function openShared(k: KitApi, def: CaveDef): void {
  unlessUnsaved(k, 'OPEN A SHARED CAVE', `OPEN ${def.name} INSTEAD?`, () => {
    k.loadDoc(docFromDef(def), undefined, false);
    k.st.say(`OPENED ${def.name}: SAVE IT TO KEEP IT`, INK.ok);
  });
}

function share(k: KitApi): void {
  const first = k.st.info.issues.find((i) => i.severity === 'error');
  if (first) {
    k.sound('error');
    return k.st.say(`FIX THE ERRORS BEFORE SHARING: ${issueLine(first)}`, INK.error);
  }
  k.sound('select');
  const def = k.st.info.def;
  k.openModal(shareModal(k, def, encodeCave(def), (d) => openShared(k, d)));
}

function library(k: KitApi): void {
  k.sound('select');
  k.openModal(libraryModal(k, (saved) => {
    unlessUnsaved(k, 'LOAD A CAVE', `LOAD ${saved.def.name} INSTEAD?`, () => {
      k.closeModal();
      k.loadDoc(docFromDef(saved.def), saved.key, true);
      k.st.say(`LOADED ${saved.def.name}`, INK.ok);
    });
  }));
}

export function runCommand(k: KitApi, c: Command): void {
  const st = k.st;
  switch (c) {
    case 'undo':
    case 'redo': {
      // A stroke in progress ends first, so it is a step of its own.
      k.painter.cancel();
      const done = c === 'undo' ? st.undo() : st.redo();
      k.sound(done ? 'move' : 'error');
      if (!done) st.say(c === 'undo' ? 'NOTHING TO UNDO' : 'NOTHING TO REDO');
      return;
    }
    case 'zoom':
      k.view = zoomTo(k.view, st.doc.grid, VIEW, k.view.cell === 16 ? 8 : 16, k.cursor);
      return k.sound('move');
    case 'grid':
      k.showGrid = !k.showGrid;
      return k.sound('move');
    case 'preview':
      k.preview = !k.preview;
      k.sound('move');
      return st.say(k.preview ? 'SHOWING WHAT THE ? CELLS ROLL AT LEVEL 1 (V TO HIDE)' : 'SHOWING THE ? CELLS');
    case 'test':
      return test(k);
    case 'save':
      return save(k);
    case 'share':
      return share(k);
    case 'new':
      return unlessUnsaved(k, 'NEW CAVE', 'START A NEW CAVE ANYWAY?', () => {
        k.loadDoc(newDoc(), undefined, true);
        k.st.say('A NEW CAVE: STEEL ALL ROUND, AN INBOX AND AN EXIT');
      });
    case 'library':
      return library(k);
    case 'help':
      k.sound('select');
      return k.openModal(helpModal(k));
    case 'back':
      return unlessUnsaved(k, 'LEAVE THE KIT', 'LEAVE ANYWAY? (THE KIT KEEPS A WORKING COPY.)', () => {
        k.st.persist();
        k.sound('back');
        k.host.go({ to: 'title', skipPress: true });
      });
    default:
  }
}

/** Ctrl+V with no dialog open: a pasted share code or link opens that cave. */
export function pasteCode(k: KitApi, text: string): void {
  if (!/SF\d+\./.test(text)) return;
  const got = decodeCave(text);
  if (got.ok) return openShared(k, got.def);
  k.sound('error');
  k.st.say(`THAT CODE DID NOT WORK: ${got.reason.toUpperCase()}`, INK.error);
}
